import { colors as themeColors } from '../utils/theme';
import { fonts } from '../utils/theme';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Button, Typography,
  Select, MenuItem, FormControl, Switch, CircularProgress, TextField,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useRights } from '../context/RightsContext';
import { getCurrentFYear } from '../utils/financialYear';

// ── Palette ───────────────────────────────────────────────────────────────────
// Matches the rest of the dashboard's modern theme (Home2.js / DashboardItems.js):
// #1877F2 blue accent, #EEF1F6 hairline borders, soft rgba(15,23,42,...) shadows,
// 14-18px radii, #0F172A / #64748B / #94A3B8 text tones.
const C = {
  blue: themeColors.primary.main,
  blueDark: themeColors.primary.darker,
  blueLight: '#EBF3FF',
  blueMid: '#D0E4FC',
  grey200: '#EEF1F6',
  grey300: themeColors.neutral.line,
  grey400: themeColors.neutral.muted,
  grey600: themeColors.neutral.sub,
  grey800: themeColors.neutral.ink,
  arctic: '#F7FAFF',
  white: themeColors.neutral.surface,
  surface: '#F3F6FC',
  danger: themeColors.error.main,
  green: '#16A34A',
};

const ios = {
  width: 34, height: 19, p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0, m: '2px',
    transition: 'transform 0.18s cubic-bezier(0.4,0,0.2,1)',
    '&.Mui-checked': {
      transform: 'translateX(15px)', color: C.white,
      '& + .MuiSwitch-track': { bgcolor: C.blue, opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: 15, height: 15, bgcolor: C.white, boxShadow: '0 1px 3px rgba(0,0,0,0.18)' },
  '& .MuiSwitch-track': { borderRadius: 10, bgcolor: C.grey300, opacity: 1 },
};

// ── Static data ───────────────────────────────────────────────────────────────
// ccode = DB Ccode (BookName) used in MasterSaleGrid — same mapping as the mobile app
// billingOnly = shown for BOTH "Billing" and "Accounting" plans;
// !billingOnly = shown ONLY for "Accounting" plan.
const BOOKS = [
  { value: 'QUOTATION', label: 'Quotation', ccode: 'QUOT', billingOnly: true },
  { value: 'CHALLAN', label: 'Sale Challan', ccode: 'CHAL', billingOnly: true },
  // { value: 'SALE_ORDER',       label: 'Sale Order',       ccode: 'SORDER' },
  { value: 'SALE_INVOICE', label: 'Sale Invoice', ccode: 'SALE', billingOnly: true },
  { value: 'PROFORMA_INVOICE', label: 'Proforma Invoice', ccode: 'PROFORMA' },
  { value: 'CREDIT_NOTE', label: 'Credit Note', ccode: 'CREDIT', billingOnly: true },
  // { value: 'PURCHASE_ORDER',   label: 'Purchase Order',   ccode: 'PORDER' },
  { value: 'PURCHASE_CHALLAN', label: 'Purchase Challan', ccode: 'PCHAL'  },
  { value: 'PURCHASE_INVOICE', label: 'Purchase Invoice', ccode: 'PURC'   },
  { value: 'CREDIT_EXPENSE',  label: 'Credit Expense',  ccode: 'CREXP'  },
  { value: 'DEBIT_NOTE',       label: 'Debit Note',       ccode: 'DEBIT'  },
];

const INITIAL_COLUMNS = [
  { id: 1, name: 'Sr No', displayLabel: null, selected: true, locked: true },
  { id: 2, name: 'Item Code', displayLabel: null, selected: true, locked: true },
  { id: 3, name: 'Description', displayLabel: null, selected: true, locked: true },
  { id: 4, name: 'Unit', displayLabel: null, selected: true, locked: true },
  { id: 5, name: 'PO No', displayLabel: null, selected: false, locked: false },
  { id: 6, name: 'PO Date', displayLabel: null, selected: false, locked: false },
  { id: 7, name: 'PO Qty', displayLabel: null, selected: false, locked: false },
  { id: 8, name: 'EX4', displayLabel: null, selected: false, locked: false },
  { id: 9, name: 'EX5', displayLabel: null, selected: false, locked: false },
  { id: 10, name: 'EXQty6', displayLabel: null, selected: false, locked: false },
  { id: 11, name: 'Type', displayLabel: null, selected: false, locked: false },
  { id: 12, name: 'Size', displayLabel: null, selected: false, locked: false },
  { id: 13, name: 'Color', displayLabel: null, selected: false, locked: false },
  { id: 14, name: 'Batch No', displayLabel: null, selected: false, locked: false },
  { id: 15, name: 'Exp. Date', displayLabel: null, selected: false, locked: false },
  { id: 16, name: 'SerialNo', displayLabel: null, selected: false, locked: false },
  { id: 17, name: 'Pkg', displayLabel: null, selected: true, locked: false },
  { id: 18, name: 'Qty', displayLabel: null, selected: true, locked: true },
  { id: 19, name: 'Free Qty', displayLabel: null, selected: false, locked: false },
  { id: 20, name: 'Rate', displayLabel: null, selected: true, locked: true },
  { id: 21, name: 'Per', displayLabel: null, selected: true, locked: false },
  { id: 22, name: 'Disc', displayLabel: null, selected: true, locked: false },
  { id: 23, name: 'Oth 1', displayLabel: null, selected: false, locked: false },
  { id: 24, name: 'Oth 2', displayLabel: null, selected: false, locked: false },
  { id: 25, name: 'Oth 3', displayLabel: null, selected: false, locked: false },
  { id: 26, name: 'Oth 4', displayLabel: null, selected: false, locked: false },
  { id: 27, name: 'Oth 5', displayLabel: null, selected: false, locked: false },
  { id: 28, name: 'Oth 6', displayLabel: null, selected: false, locked: false },
  { id: 29, name: 'Oth 7', displayLabel: null, selected: false, locked: false },
  { id: 30, name: 'Tax Code', displayLabel: null, selected: true, locked: true },
  { id: 31, name: 'UTGST', displayLabel: null, selected: false, locked: false },
  { id: 32, name: 'GST Cess', displayLabel: null, selected: false, locked: false },
  { id: 33, name: 'GST Oth', displayLabel: null, selected: false, locked: false },
  { id: 34, name: 'Amount', displayLabel: null, selected: true, locked: true },
  { id: 35, name: 'Remark', displayLabel: null, selected: true, locked: true },
];

const BLANK_ASKROWS = [
  { label: '', rupeesMode: false, lessMode: false },
  { label: '', rupeesMode: false, lessMode: false },
  { label: '', rupeesMode: false, lessMode: false },
  { label: '', rupeesMode: false, lessMode: false },
  { label: '', rupeesMode: false, lessMode: false },
  { label: '', rupeesMode: false, lessMode: false },
  { label: '', rupeesMode: false, lessMode: false },
];

// OthColName is stored on the Oth 1-7 rows (ColNo 23-29)
const OTH_IDS = [23, 24, 25, 26, 27, 28, 29];

// ── Page ─────────────────────────────────────────────────────────────────────
export default function UserDefineColumns({ connectionString }) {
  const { selectedCompany, user } = useAuth();
  const companyId = selectedCompany?.CompId;
  const userId = user?.UserId;
  const navigate = useNavigate();
  const { canAccess, showAccessDenied, rightsReady, rightsLoading } = useRights();

  // ── Plan-based book visibility ──────────────────────────────────────────────
  // "Billing" plan  -> Quotation, Sale Challan, Sale Invoice, Credit Note only.
  // "Accounting" plan (or any other/unknown plan) -> every book, unchanged.
  const planName = selectedCompany?.planName;
  const visibleBooks = useMemo(
    () => (planName === 'Billing' ? BOOKS.filter(b => b.billingOnly) : BOOKS),
    [planName]
  );

  useEffect(() => {
    if (!rightsReady || rightsLoading) return;
    if (canAccess && !canAccess('UserDefine', 'view')) {
      showAccessDenied?.();
      navigate('/main/home', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightsReady, rightsLoading]);

  const [selectedBook, setSelectedBook] = useState('SALE_INVOICE');
  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [askRows, setAskRows] = useState(BLANK_ASKROWS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);


  // Cache all fetched data so book-switches don't trigger re-fetch
  const allDataRef = useRef({});

  // ── Apply DB rows to UI state ─────────────────────────────────────────────
  const applyBookData = useCallback((book, grouped) => {
    const ccode = BOOKS.find(b => b.value === book)?.ccode || book;
    const rows = grouped[ccode];
    if (!rows || rows.length === 0) {
      setColumns(INITIAL_COLUMNS);
      setAskRows(BLANK_ASKROWS);
      return;
    }

    // Map API response directly to component state
    // ColNo → id, DisplayLabel → displayLabel, IsColumnlocked → locked, ColToBeAsk → selected
    setColumns(rows.map(dbRow => ({
      id: Number(dbRow.ColNo),
      name: dbRow.ColName || '', // Keep for reference (original name in DB)
      displayLabel: dbRow.DisplayLabel || null, // User's custom label
      selected: Boolean(dbRow.ColToBeAsk),
      locked: Boolean(dbRow.IsColumnlocked),
    })).sort((a, b) => a.id - b.id)); // Sort by ColNo to maintain order

    // Extract OthColName / RupessToBeAsk / AddLess for the 7 Oth columns
    setAskRows(OTH_IDS.map(colId => {
      const dbRow = rows.find(r => Number(r.ColNo) === colId);
      return dbRow
        ? { label: dbRow.OthColName || '', rupeesMode: Boolean(dbRow.RupessToBeAsk), lessMode: Boolean(dbRow.AddLess) }
        : { label: '', rupeesMode: false, lessMode: false };
    }));
  }, []);

  // ── Fetch all books on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !connectionString) return;
    setLoading(true);
    axios.get(`${connectionString}/api/mastersalegrid`, { params: { companyId } })
      .then(res => {
        const data = res.data || {};
        allDataRef.current = data;
        applyBookData(selectedBook, data);

      })
      .catch(err => console.error('UserDefineColumns fetch error:', err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, connectionString]);

  // If the plan changes (or the current selection isn't visible for this
  // plan), fall back to the first book the plan allows.
  useEffect(() => {
    if (visibleBooks.length > 0 && !visibleBooks.some(b => b.value === selectedBook)) {
      setSelectedBook(visibleBooks[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleBooks]);

  // ── Apply cached data when book changes ──────────────────────────────────
  useEffect(() => {
    if (Object.keys(allDataRef.current).length > 0) {
      applyBookData(selectedBook, allDataRef.current);
    }
  }, [selectedBook, applyBookData]);


  // ── Column toggle helpers ─────────────────────────────────────────────────
  const toggle = (id) => {
    const col = columns.find(c => c.id === id);
    // Prevent unchecking locked columns
    if (col?.locked) return;
    setColumns(p => p.map(c => c.id === id ? { ...c, selected: !c.selected } : c));
  };
  const selectAll = () => setColumns(p => p.map(c => ({ ...c, selected: true })));
  const clearAll = () => setColumns(p => p.map(c =>
    c.locked ? c : { ...c, selected: false }
  ));
  const updateDisplayLabel = (id, newLabel) => {
    setColumns(p => p.map(c => c.id === id ? { ...c, displayLabel: newLabel || null } : c));
  };

  // Oth 1-7 columns currently enabled (toggled on in Column Details) —
  // drives both the Taxable section's visibility and which of its rows show.
  const enabledOthIds = useMemo(
    () => OTH_IDS.filter(id => columns.find(c => c.id === id)?.selected),
    [columns]
  );

  // Taxable section is the single source of truth for the Oth label; keep the
  // matching Column Labels field (which is read-only) in sync as the user types.
  const updateAskRow = (othIdx, val) => {
    setAskRows(prev => prev.map((r, j) => j === othIdx ? val : r));
    updateDisplayLabel(OTH_IDS[othIdx], val.label);
  };


  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!companyId) return;

    setSaving(true);
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const ccode = BOOKS.find(b => b.value === selectedBook)?.ccode || selectedBook;
    const fYear = getCurrentFYear();

    const rows = columns.map((col, idx) => {
      const othIdx = OTH_IDS.indexOf(col.id);
      const ask = othIdx >= 0 ? askRows[othIdx] : null;
      return {
        CompID: companyId,
        BookName: ccode,
        SrNo: idx + 1,
        ColNo: col.id,
        ColName: col.name,
        ColToBeAsk: col.selected,
        DisplayLabel: col.displayLabel || null,
        OthColName: ask ? (ask.label || null) : null,
        RupessToBeAsk: ask ? ask.rupeesMode : false,
        Taxable: ask ? col.selected : false,
        AddLess: ask ? ask.lessMode : false,
        Exciseable: false,
        Ccode: 1,
        Colwidth: 800,
        FixAlign: 4,
        ColAlign: 1,
        RefId: 0,
        AddUserId: userId ?? null,
        AddUserDateTime: now,
        EditUserId: userId ?? null,
        EditUserDateTime: now,
        FYear: fYear,
        BranchId: null,
      };
    });

    try {
      await axios.post(`${connectionString}/api/mastersalegrid`, { companyId, bookName: ccode, rows, fYear });
      allDataRef.current = { ...allDataRef.current, [ccode]: rows };
      navigate(-1);
    } catch (err) {
      console.error('UserDefineColumns save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Cancel — restore last saved/fetched state ─────────────────────────────
  const handleCancel = () => {
    applyBookData(selectedBook, allDataRef.current);
  };

  const on = columns.filter(c => c.selected).length;
  const bookLabel = BOOKS.find(b => b.value === selectedBook)?.label ?? '';

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F3F6FC 0%, #EEF2FA 100%)', display: 'flex', flexDirection: 'column', fontFamily: fonts.family, }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.darker} 100%)`,
        px: 3, py: 1.6, display: 'flex', alignItems: 'center', gap: 2, minHeight: 64,
        boxShadow: '0 10px 28px rgba(24,119,242,0.25)',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '11px',
          bgcolor: 'rgba(255,255,255,0.16)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <ViewColumnIcon sx={{ fontSize: '1.15rem', color: C.white }} />
        </Box>
        <Box sx={{ mr: 'auto' }}>
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: C.white, lineHeight: 1.3, letterSpacing: '0.1px' }}>
            User Define Columns
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4, mt: '1px' }}>
            Configure visible columns per book type
          </Typography>
        </Box>

        {/* Book selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
            Book Type
          </Typography>
          <FormControl size="small">
            <Select
              value={selectedBook}
              onChange={e => setSelectedBook(e.target.value)}
              disabled={loading}
              sx={{
                minWidth: 165, fontSize: '0.8rem', fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.14)',
                borderRadius: '10px',
                '& .MuiSelect-select': { py: '7px', px: '12px', color: C.white },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.22)' },
                '& .MuiSelect-icon': { color: C.white },
                transition: 'background 0.15s',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.75, borderRadius: '14px',
                    boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
                    border: `1px solid ${C.grey200}`,
                  },
                },
              }}
            >
              {visibleBooks.map(b => (
                <MenuItem
                  key={b.value} value={b.value}
                  sx={{
                    fontSize: '0.8rem', mx: 0.75, my: 0.25, borderRadius: '8px',
                    color: b.value === selectedBook ? C.blue : C.grey800,
                    fontWeight: b.value === selectedBook ? 700 : 500,
                    '&.Mui-selected': { bgcolor: C.blueLight, color: C.blue },
                    '&.Mui-selected:hover': { bgcolor: C.blueMid, color: C.blue },
                    '&:hover': { bgcolor: C.blueLight, color: C.blue },
                  }}
                >
                  {b.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button
          size="small" onClick={handleCancel} disabled={loading || saving}
          sx={{
            fontSize: '0.75rem', textTransform: 'none', fontWeight: 600,
            color: 'rgba(255,255,255,0.8)', borderRadius: '9px', px: 1.8, py: 0.65,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', color: C.white },
          }}
        >
          Cancel
        </Button>
        <Button
          size="small" variant="contained"
          startIcon={saving ? <CircularProgress size={12} sx={{ color: C.blue }} /> : <SaveIcon sx={{ fontSize: '0.85rem' }} />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{
            fontSize: '0.75rem', textTransform: 'none', fontWeight: 700,
            bgcolor: C.white, color: C.blue,
            borderRadius: '9px', px: 2.4, py: 0.65,
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
            '&:hover': { bgcolor: C.arctic, boxShadow: '0 8px 20px rgba(0,0,0,0.16)' },
            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.5)', color: C.blue },
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2.5, p: 2.5, alignItems: 'flex-start', position: 'relative' }}>

        {/* Loading overlay */}
        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(243,246,252,0.8)', backdropFilter: 'blur(4px)',
          }}>
            <CircularProgress size={32} sx={{ color: C.blue }} />
          </Box>
        )}

        {/* ── Column Details panel ─────────────────────────────────────────── */}
        <Box sx={{
          width: 300, flexShrink: 0,
          bgcolor: C.white,
          border: `1px solid ${C.grey200}`,
          borderRadius: '18px',
          boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>

          <Box sx={{
            px: 2.25, py: 1.6,
            background: `linear-gradient(160deg, ${themeColors.neutral.surface} 0%, #F7FAFF 100%)`,
            borderBottom: `1px solid ${C.grey200}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: C.grey800 }}>
                Column Details
              </Typography>
              <Box sx={{
                px: '9px', py: '2px', borderRadius: '20px',
                background: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.darker} 100%)`,
                boxShadow: '0 4px 12px rgba(24,119,242,0.3)',
              }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: C.white, lineHeight: 1.7 }}>
                  {on} / {INITIAL_COLUMNS.length}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: C.grey200, borderRadius: '9px', p: '2px' }}>
              <Button size="small" onClick={selectAll} sx={{
                fontSize: '0.64rem', textTransform: 'none', color: C.blue, fontWeight: 700,
                px: 1, py: 0.3, minWidth: 0, borderRadius: '7px',
                bgcolor: C.white, boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
                '&:hover': { bgcolor: C.white },
              }}>All</Button>
              <Button size="small" onClick={clearAll} sx={{
                fontSize: '0.64rem', textTransform: 'none', color: C.grey600, fontWeight: 600,
                px: 1, py: 0.3, minWidth: 0, borderRadius: '7px',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              }}>None</Button>
            </Box>
          </Box>

          <Box sx={{
            overflowY: 'auto', maxHeight: 'calc(100vh - 210px)',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '3px' },
          }}>
            {columns.map((col, idx) => (
              <Box
                key={col.id}
                onClick={() => !col.locked && toggle(col.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.2,
                  px: 2.25, py: '9px',
                  borderBottom: idx < columns.length - 1 ? `1px solid ${C.grey200}` : 'none',
                  cursor: col.locked ? 'not-allowed' : 'pointer',
                  bgcolor: col.selected ? (col.locked ? 'rgba(24,119,242,0.07)' : C.blueLight) : C.white,
                  transition: 'background 0.15s',
                  opacity: col.locked ? 0.95 : 1,
                  '&:hover': {
                    bgcolor: col.locked ? 'rgba(24,119,242,0.07)' : (col.selected ? C.blueMid : C.arctic),
                  },
                }}
              >
                <Typography sx={{
                  fontSize: '0.6rem', color: C.grey400, fontWeight: 600,
                  fontFamily: 'monospace', minWidth: 18, lineHeight: 1,
                }}>
                  {String(col.id).padStart(2, '0')}
                </Typography>
                <Typography sx={{
                  flex: 1, fontSize: '0.8rem',
                  fontWeight: col.selected ? 700 : 500,
                  color: col.selected ? C.blue : C.grey800,
                  userSelect: 'none',
                  transition: 'color 0.15s',
                }}>
                  {col.displayLabel || col.name}
                  {col.locked && (
                    <span style={{ fontSize: '0.62rem', color: C.grey400, marginLeft: '6px', fontWeight: 500 }}>
                      Default
                    </span>
                  )}
                </Typography>
                <Switch
                  checked={col.selected}
                  onChange={() => !col.locked && toggle(col.id)}
                  onClick={e => e.stopPropagation()}
                  disabled={col.locked}
                  size="small"
                  sx={{
                    ...ios,
                    opacity: col.locked ? 0.5 : 1,
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Right column ──────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Stat cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1.5 }}>
            {[
              { label: 'Book Type', value: bookLabel, accent: false },
              { label: 'Columns Visible', value: `${on} of ${INITIAL_COLUMNS.length}`, accent: true },
            ].map(s => (
              <Box key={s.label} sx={{
                bgcolor: s.accent ? undefined : C.white,
                background: s.accent ? `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.darker} 100%)` : undefined,
                border: s.accent ? 'none' : `1px solid ${C.grey200}`,
                borderRadius: '16px', px: 2.25, py: 1.75,
                boxShadow: s.accent ? '0 10px 24px rgba(24,119,242,0.28)' : '0 6px 18px rgba(15,23,42,0.05)',
              }}>
                <Typography sx={{ fontSize: '0.62rem', color: s.accent ? 'rgba(255,255,255,0.75)' : C.grey400, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, mb: 0.6 }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: s.accent ? C.white : C.grey800, lineHeight: 1 }}>
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Taxable panel — only shown once an Oth 1-7 column is enabled;
              lists one row per enabled Oth column only. */}
          {enabledOthIds.length > 0 && (
            <Box sx={{
              bgcolor: C.white,
              border: `1px solid ${C.grey200}`,
              borderRadius: '18px',
              boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
              overflow: 'hidden',
            }}>
              <Box sx={{
                px: 2.25, py: 1.6,
                background: `linear-gradient(160deg, ${themeColors.neutral.surface} 0%, #F7FAFF 100%)`,
                borderBottom: `1px solid ${C.grey200}`,
              }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: C.grey800 }}>
                  Taxable
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: C.grey400, mt: 0.3 }}>
                  Configure prompt fields for data entry
                </Typography>
              </Box>

              <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {enabledOthIds.map(othId => {
                  const othIdx = OTH_IDS.indexOf(othId);
                  const row = askRows[othIdx];
                  return (
                    <AskRow
                      key={othId}
                      index={othIdx + 1}
                      label={row.label}
                      rupeesMode={row.rupeesMode}
                      lessMode={row.lessMode}
                      onChange={val => updateAskRow(othIdx, val)}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Column Labels panel — rename columns */}
          <Box sx={{
            bgcolor: C.white,
            border: `1px solid ${C.grey200}`,
            borderRadius: '18px',
            boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
            overflow: 'hidden',
          }}>
            <Box sx={{
              px: 2.25, py: 1.6,
              background: `linear-gradient(160deg, ${themeColors.neutral.surface} 0%, #F7FAFF 100%)`,
              borderBottom: `1px solid ${C.grey200}`,
            }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: C.grey800 }}>
                Column Labels
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: C.grey400, mt: 0.3 }}>
                Customize display names (leave blank for default)
              </Typography>
            </Box>

            <Box sx={{
              p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.1, maxHeight: '240px', overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '3px' },
            }}>
              {columns.filter(col => !col.locked).map(col => {
                const isOth = OTH_IDS.includes(col.id);
                return (
                  <Box key={col.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.grey600 }}>
                      {col.name}
                    </Typography>
                    <TextField
                      size="small"
                      disabled={isOth}
                      placeholder={isOth ? 'Set from the Taxable section' : `Default: ${col.name}`}
                      value={col.displayLabel || ''}
                      onChange={e => updateDisplayLabel(col.id, e.target.value)}
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '0.78rem', py: '7px' },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '9px', bgcolor: C.arctic,
                          '& fieldset': { borderColor: C.grey200 },
                          '&:hover fieldset': { borderColor: C.blueMid },
                          '&.Mui-focused fieldset': { borderColor: C.blue, borderWidth: '1.5px' },
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Quick actions */}
          <Box sx={{
            bgcolor: C.white,
            border: `1px solid ${C.grey200}`,
            borderRadius: '18px',
            boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
            px: 2.25, py: 1.75,
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          }}>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: C.grey400, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Apply to
            </Typography>
            {['All Columns', 'All Tax Details'].map(label => (
              <Button key={label} variant="outlined" size="small"
                onClick={label === 'All Columns' ? selectAll : undefined}
                sx={{
                  fontSize: '0.74rem', textTransform: 'none', fontWeight: 700,
                  borderColor: C.grey200, color: C.grey600, borderRadius: '10px', px: 2, py: 0.5,
                  '&:hover': { borderColor: C.blue, color: C.blue, bgcolor: C.blueLight },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        bgcolor: C.white,
        borderTop: `1px solid ${C.grey200}`,
        px: 3, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 1.2,
        boxShadow: '0 -4px 16px rgba(15,23,42,0.04)',
      }}>
        <Typography sx={{ fontSize: '0.72rem', color: C.grey400, mr: 'auto', fontWeight: 500 }}>
          {on} column{on !== 1 ? 's' : ''} visible · {bookLabel}
        </Typography>
        <Button size="small" onClick={handleCancel} disabled={loading || saving} sx={{
          fontSize: '0.78rem', textTransform: 'none', fontWeight: 700,
          color: C.grey600, borderRadius: '10px', px: 2.2, py: 0.7,
          '&:hover': { bgcolor: C.grey200, color: C.danger },
        }}>
          Cancel
        </Button>
        <Button
          variant="contained" size="small"
          startIcon={saving ? <CircularProgress size={13} sx={{ color: C.white }} /> : <SaveIcon sx={{ fontSize: '0.9rem' }} />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{
            fontSize: '0.78rem', textTransform: 'none', fontWeight: 700,
            background: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.darker} 100%)`,
            borderRadius: '10px', px: 3, py: 0.7,
            boxShadow: '0 8px 20px rgba(24,119,242,0.3)',
            '&:hover': { boxShadow: '0 10px 24px rgba(24,119,242,0.4)' },
          }}
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}

// ── AskRow — controlled by parent ────────────────────────────────────────────
// rupeesMode: false = "Percentage" (RupessToBeAsk=0), true = "Rupees" (RupessToBeAsk=1)
// lessMode:   false = "+" (AddLess=0), true = "-" (AddLess=1)
function AskRow({ index, label, rupeesMode, lessMode, onChange }) {
  const selectSx = {
    fontSize: '0.75rem',
    '& .MuiSelect-select': { py: '6px', px: '10px' },
  };
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '1fr 110px 70px',
      gap: 1, alignItems: 'center',
      px: 0.75, py: 0.4, borderRadius: '9px',
      transition: 'background 0.15s',
      '&:hover': { bgcolor: C.arctic },
    }}>
      <Box
        component="input"
        value={label}
        onChange={e => onChange({ label: e.target.value, rupeesMode, lessMode })}
        placeholder={`Field ${index}`}
        sx={{
          border: `1px solid ${C.grey200}`,
          borderRadius: '9px',
          px: 1.4, py: '7px',
          fontSize: '0.78rem',
          color: C.grey800,
          bgcolor: C.arctic,
          outline: 'none',
          fontFamily: 'inherit',
          '&:focus': {
            borderColor: C.blue,
            bgcolor: C.white,
            boxShadow: `0 0 0 3px rgba(24,119,242,0.15)`,
          },
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
      />
      <Select
        size="small"
        value={rupeesMode ? 'Rupees' : 'Percentage'}
        onChange={e => onChange({ label, rupeesMode: e.target.value === 'Rupees', lessMode })}
        sx={selectSx}
      >
        <MenuItem value="Percentage" sx={{ fontSize: '0.78rem' }}>Percentage</MenuItem>
        <MenuItem value="Rupees" sx={{ fontSize: '0.78rem' }}>Rupees</MenuItem>
      </Select>
      <Select
        size="small"
        value={lessMode ? '-' : '+'}
        onChange={e => onChange({ label, rupeesMode, lessMode: e.target.value === '-' })}
        sx={selectSx}
      >
        <MenuItem value="+" sx={{ fontSize: '0.78rem' }}>+</MenuItem>
        <MenuItem value="-" sx={{ fontSize: '0.78rem' }}>-</MenuItem>
      </Select>
    </Box>
  );
}
